import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import actions from "@/actions";
import { Input, Modal } from "@/components";

import {
    Footer,
    FormRow,
    InputContainer,
    SectionDefaultLightLevels,
    SectionLightLevels,
    SectionSensorSettings,
} from ".";

const title = "Group Settings";

const FormContent = styled.div`
    border-bottom: 1px solid #ebebeb;
    padding: 1em;
`;

const Divider = styled.div`
    border-bottom: 1px solid #ebebeb;
    margin-bottom: 1rem;
`;

const SettingsModal = ({
    onClose,
    visible,
    settings,
    updateSettings,
    lightGroups,
    setLightGroups,
    onSaveAndPublish,
    onSave,
}) => {
    // These local settings are used for performance reasons; to prevent spamming redux with every slider change
    const [localSettings, setLocalSettings] = useState(settings);
    const [prevSettings, setPrevSettings] = useState(settings);
    const updateLocalSettings = (newSettings) =>
        setLocalSettings({ ...localSettings, ...newSettings });
    const commitSettings = () => updateSettings(localSettings);

    if (settings !== prevSettings) {
        setLocalSettings(settings);
        setPrevSettings(settings);
    }

    const groupName = useMemo(
        () =>
            lightGroups.find((g) => g.groupID === settings.groupID)
                ?.groupName ?? "",
        [lightGroups, settings.groupID],
    );

    const updateGroupName = (name) => {
        const newGroups = lightGroups.map((g) =>
            g.groupID === settings.groupID ? { ...g, groupName: name } : g,
        );
        return setLightGroups(newGroups);
    };

    const slotProps = {
        paper: {
            sx: {
                maxWidth: "50em",
                width: "50rem",
            },
        },
    };

    return (
        <Modal
            visible={visible}
            title={title}
            onClose={onClose}
            slotProps={slotProps}
        >
            <form>
                <FormContent>
                    <FormRow>
                        <InputContainer>
                            <Input
                                label="Group Name"
                                required
                                value={groupName}
                                onChange={updateGroupName}
                            />
                        </InputContainer>
                    </FormRow>
                    <Divider />
                    <SectionLightLevels
                        localSettings={localSettings}
                        updateLocalSettings={updateLocalSettings}
                        commitSettings={commitSettings}
                    />
                    <Divider />
                    <SectionSensorSettings
                        settings={settings}
                        updateSettings={updateSettings}
                    />
                    <Divider />
                    <SectionDefaultLightLevels
                        localSettings={localSettings}
                        updateLocalSettings={updateLocalSettings}
                        commitSettings={commitSettings}
                    />
                </FormContent>
                <Footer
                    settings={settings}
                    onClose={onClose}
                    onSave={onSave}
                    onSaveAndPublish={onSaveAndPublish}
                    canSave={!!groupName.trim()}
                />
            </form>
        </Modal>
    );
};

SettingsModal.propTypes = {
    onClose: PropTypes.func,
    visible: PropTypes.bool,
    settings: PropTypes.object,
    updateSettings: PropTypes.func,
    lightGroups: PropTypes.array,
    setLightGroups: PropTypes.func,
    onSaveAndPublish: PropTypes.func,
    onSave: PropTypes.func,
};

const mapStateToProps = ({ map }) => ({
    settings: map.general.editingLightGroupSettings,
    lightGroups: map.general.lightGroups,
});

const mapDispatchToProps = {
    updateSettings: actions.map.general.updateEditingLightGroupSettings,
    setLightGroups: actions.map.general.setLightGroups,
};

export default connect(mapStateToProps, mapDispatchToProps)(SettingsModal);
